import React from "react";
import styled from "styled-components";
import Form from "manifest-components/src/Form/Form";
import { Button } from "manifest-components/src/Button";
import { Input } from "manifest-components/src/Inputs/Input";
import { PasswordInputWithValidation } from "manifest-components/src/Inputs/PasswordInputWithValidation";
import { ColorVariables } from "manifest-components/src/Colors";
import { Container, FlexContainer, Spacing } from "manifest-components/src/Containers";
import DisclaimerCheckboxAgreeToTermsOfService from "manifest-components/src/LegalDisclaimer/employerDashboardDisclaimers/DisclaimerAgreeToTermsOfService";
import type { CreateAccountPageControllerType } from "./CreateAccountPageController";
import FullSizeBoxContainer from "../../components/Layout/FullSizeBoxContainer";

export type AccountCreationScreenProps = CreateAccountPageControllerType;

const ErrorMessage = styled.p`
  color: ${ColorVariables.cherry};
`;

const AccountCreationScreen = ({
  fieldValidations,
  accountDetails,
  updateAccountDetail,
  error,
  handleContinue,
  setIsDisclaimerChecked,
  setPasswordValid,
  setConfirmPasswordValid,
}: AccountCreationScreenProps) => (
  <FullSizeBoxContainer>
    <Spacing space={30} />
    <Form submit={handleContinue}>
      <Container className="tile-section-a" width={600} style={{ margin: "auto" }}>
        <Container className="fullWidth body-section-a">
          <h3 className="center-on-desktop">Get started</h3>
        </Container>
        <Spacing space={60} />
        <Container className="tile-section-a">
          {fieldValidations.errorMessages.disclaimerChecked ? (
            <ErrorMessage data-error="true" className="medium error">
              Please indicate that you have read and agree to the Terms of Service, Privacy Policy, and User Agreement
            </ErrorMessage>
          ) : (
            error && (
              <ErrorMessage data-error="true" className="medium error">
                An account already exists using this email or mobile number. Please log in or try again.
              </ErrorMessage>
            )
          )}
          <FlexContainer>
            <Input
              name="firstName"
              error={!!fieldValidations.errorMessages.firstName}
              caption={fieldValidations.errorMessages.firstName}
              handleBlur={() => fieldValidations.validateField("firstName")}
              onChange={updateAccountDetail("firstName")}
              title="First name"
              value={accountDetails.firstName}
              autoComplete="given-name"
              style={{ marginRight: "15px" }}
            />
            <Input
              name="lastName"
              error={!!fieldValidations.errorMessages.lastName}
              caption={fieldValidations.errorMessages.lastName}
              handleBlur={() => fieldValidations.validateField("lastName")}
              onChange={updateAccountDetail("lastName")}
              title="Last name"
              value={accountDetails.lastName}
              autoComplete="family-name"
            />
          </FlexContainer>
          <Input
            name="companyName"
            error={!!fieldValidations.errorMessages.companyName}
            caption={fieldValidations.errorMessages.companyName}
            handleBlur={() => fieldValidations.validateField("companyName")}
            onChange={updateAccountDetail("companyName")}
            title="Company name"
            value={accountDetails.companyName}
            autoComplete="company"
          />
          <Input
            name="email"
            error={!!fieldValidations.errorMessages.workEmail}
            caption={fieldValidations.errorMessages.workEmail}
            handleBlur={() => fieldValidations.validateField("workEmail")}
            onChange={updateAccountDetail("email")}
            title="Work email"
            value={accountDetails.email}
            autoComplete="email"
          />
          <Input
            name="phone"
            type="phone"
            caption={fieldValidations.errorMessages.phoneNumber}
            error={!!fieldValidations.errorMessages.phoneNumber}
            handleBlur={() => fieldValidations.validateField("phoneNumber")}
            onChange={updateAccountDetail("phoneNumber")}
            title="Phone number"
            value={accountDetails.phoneNumber}
            autoComplete="tel"
          />
          <PasswordInputWithValidation
            name="password"
            error={!!fieldValidations.errorMessages.password}
            data-heap-redact-text="true"
            onChange={updateAccountDetail("password")}
            title="Password"
            value={accountDetails.password}
            setValid={setPasswordValid}
            handleBlur={() => fieldValidations.validateField("password")}
          />
          <PasswordInputWithValidation
            isConfirm
            name="confirmPassword"
            error={!!fieldValidations.errorMessages.confirmPassword}
            data-heap-redact-text="true"
            onChange={updateAccountDetail("confirmPassword")}
            title="Confirm password"
            value={accountDetails.confirmPassword}
            password2={accountDetails.password}
            setValid={setConfirmPasswordValid}
            handleBlur={() => fieldValidations.validateField("confirmPassword")}
          />
          <Container className="body-section-b">
            <DisclaimerCheckboxAgreeToTermsOfService
              error={!fieldValidations.validations.disclaimerChecked}
              setIsDisclaimerChecked={setIsDisclaimerChecked}
            />
          </Container>
        </Container>

        <Button name="createAccount" type="submit" className="button-section" text="Create account" />
      </Container>
    </Form>
  </FullSizeBoxContainer>
);

export { AccountCreationScreen };
